import { Box, Button, Divider, Flex, Spinner, Text } from "@chakra-ui/react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useListMyNotificationsQuery, useMarkNotificationsAsPeekedMutation, useMarkNotificationsAsViewedMutation } from "../../../autogen/bff-api";
import { ErrorBoundary } from "../../errors/ErrorBoundary/ErrorBoundary";
import { useApiError } from "../../errors/useApiError";
import { useLoggedInWithOrgContextState } from "../../auth/useLoggedInWithOrgContextState";
import { urls } from "../../../urls";
import { NotificationRow } from "./NotificationRow";

interface Props { onClose: () => void; }

export const NotificationDropdown = ({ onClose }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const displayer = useApiError();
  const authState = useLoggedInWithOrgContextState();
  const { data, isError, isLoading } = useListMyNotificationsQuery({ limit: 8 });
  const { data: unseenData } = useListMyNotificationsQuery({ unseen: true });
  const [markAsPeeked] = useMarkNotificationsAsPeekedMutation();
  const [markAsViewed] = useMarkNotificationsAsViewedMutation();
  const notifications = data?.notifications ?? [];

  // Opening the panel marks the shown items as seen (peek) — clears the bell badge.
  useEffect(() => {
    const unpeeked = notifications.filter((n) => !n.peeked).map((n) => n.id);
    // Background bookkeeping: swallow failures (no toast) but consume the promise so a rejection isn't unhandled.
    if (unpeeked.length > 0) void markAsPeeked({ markNotificationsRequest: { notificationIds: unpeeked } }).unwrap().catch(() => undefined);
    // Keyed on the query result, not the derived list (new array each render) — peek once per data change, not every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const markAllIds = Array.from(
    new Set([
      ...notifications.filter((n) => !n.viewed).map((n) => n.id),
      ...(unseenData?.notifications ?? []).map((n) => n.id),
    ])
  );

  return (
    <Box w={{ base: "100vw", sm: "380px" }} maxW="380px">
      <Flex align="center" justify="space-between" px="4" py="3">
        <Text fontWeight="bold" fontSize="md" color="smText">
          {t("Alerts")} {markAllIds.length > 0 && <Text as="span" color="smMuted" fontWeight="normal">· {t("{{count}} new", { count: markAllIds.length })}</Text>}
        </Text>
        {markAllIds.length > 0 && (
          <Button
            variant="link"
            size="sm"
            colorScheme="teal"
            onClick={async () => {
              try {
                await markAsViewed({ markNotificationsRequest: { notificationIds: markAllIds } }).unwrap();
              } catch (e) {
                displayer.triggerSoft(e);
              }
            }}
          >
            {t("Mark all as read")}
          </Button>
        )}
      </Flex>
      <Divider borderColor="smBorder" />
      <Box maxH="420px" overflowY="auto">
        {/* Loading before empty: "you're all caught up" is an answer, and it must not be given
            before the alerts have been fetched — it flashed on every open. */}
        {isLoading ? (
          <Flex py="10" justify="center"><Spinner color="smPrimary" size="sm" /></Flex>
        ) : isError ? (
          <Flex py="10" justify="center"><Text color="smError" fontSize="sm">{t("Could not load alerts.")}</Text></Flex>
        ) : notifications.length === 0 ? (
          <Flex py="10" justify="center"><Text color="smMuted" fontSize="sm">{t("You're all caught up")}</Text></Flex>
        ) : (
          notifications.map((n, i) => (
            <Box key={n.id} borderTop={i === 0 ? "none" : "1px solid"} borderColor="smBorder">
              <ErrorBoundary
                scope={`notification-row-${n.id}`}
                fallback={<Flex px="4" py="3"><Text color="smMuted" fontSize="sm">{t("Couldn't show this alert.")}</Text></Flex>}
              >
                <NotificationRow notification={n} selectedOrgId={authState.selectedOrg.id} onNavigate={onClose} />
              </ErrorBoundary>
            </Box>
          ))
        )}
      </Box>
      <Divider borderColor="smBorder" />
      <Flex justify="center" py="2.5">
        <Button variant="link" size="sm" colorScheme="teal" onClick={() => { navigate(urls.v2.notifications); onClose(); }}>
          {t("See all alerts")} →
        </Button>
      </Flex>
    </Box>
  );
};
